import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../hooks/useLanguage';
import { useScrollReveal } from '../hooks/useScrollReveal';
export function MobileAppPage() {
  const {
    t,
    setLocale,
    direction
  } = useLanguage();
  const location = useLocation();
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useScrollReveal();
  const {
    ref: descRef,
    isVisible: descVisible
  } = useScrollReveal();
  const {
    ref: featuresRef,
    isVisible: featuresVisible
  } = useScrollReveal();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useScrollReveal();
  useEffect(() => {
    if (location.pathname.startsWith('/ar')) {
      setLocale('ar');
    } else {
      setLocale('en');
    }
    window.scrollTo(0, 0);
  }, [location.pathname, setLocale]);
  return <div className="min-h-screen w-full bg-white text-slate-900" dir={direction} data-id="element-783">
      <Header variant="transparent-dark" data-id="element-784" />

      <main data-id="element-785">
        {/* Wrapper for hero SVG spanning hero + next sections */}
        <div className="relative" data-id="element-786">
          {/* 1. Hero Banner */}
          <section className="relative min-h-screen overflow-hidden" ref={heroRef} aria-label="Mobile App Hero" data-id="element-787">
            {/* Background Image */}
            <div className="absolute inset-0 bg-cover bg-center" role="presentation" aria-hidden="true" style={{
            backgroundImage: "url('https://cdn.magicpatterns.com/uploads/rFQfaAYt29F6r1t8Zk75YM/mobile-app-hero.jpg')"
          }} data-id="element-788" />

            <div className="relative z-10 w-full min-h-screen flex items-center" data-id="element-789">
              <div className="container mx-auto px-6 md:px-12" data-id="element-790">
                <div className="max-w-3xl" data-id="element-791">
                  <h1 className={`text-[#061D76] font-semibold leading-[1.05] mb-10 uppercase text-[32px] md:text-[44px] lg:text-[55px] whitespace-pre-line reveal-hidden ${heroVisible ? 'animate-fade-up' : ''}`} data-id="element-792">
                    {t('mobileAppPage.hero.title')}
                  </h1>
                  <p className={`text-base md:text-lg text-[#061D76] mb-10 max-w-xl leading-relaxed reveal-hidden ${heroVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-793">
                    {t('mobileAppPage.hero.subtitle')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 2. Description Section - Dark gradient with text only */}
          <section className="section-inner gradient-dark-horizontal text-white relative overflow-hidden z-[2]" ref={descRef} aria-label="Mobile App Overview" data-id="element-794">
            <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-795">
              <div className="max-w-5xl" data-id="element-796">
                <p className={`text-lg md:text-xl leading-relaxed reveal-hidden ${descVisible ? 'animate-fade-up' : ''}`} data-id="element-797">
                  {t('mobileAppPage.description.paragraph1')}
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* 3. App Features Section */}
        <section className="gradient-light relative overflow-hidden" ref={featuresRef} aria-label="App Features" style={{
        paddingTop: '4rem',
        paddingBottom: 0,
        minHeight: '90vh'
      }} data-id="element-798">
          {/* Bottom-right decorative SVG */}
          <img src="https://cdn.magicpatterns.com/uploads/gfFDiAypfwkoAc35tX4FM3/mobile-bottom-right.svg" alt="" role="presentation" aria-hidden="true" className="absolute bottom-0 right-0 pointer-events-none h-[70%] w-auto" data-id="element-799" />

          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-800">
            {/* Section Label */}
            <span className={`text-[#25B8D6] font-semibold text-lg mb-4 block reveal-hidden ${featuresVisible ? 'animate-fade-up' : ''}`} data-id="element-801">
              {t('mobileAppPage.appFeatures.label')}
            </span>

            {/* 3-column layout using flex for precise alignment */}
            <div className="flex flex-col md:flex-row" data-id="element-802">
              {/* Left Column - vertically centered */}
              <div className={`flex-1 flex items-center order-1 md:order-1 reveal-hidden ${featuresVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-803">
                <div className="space-y-6 py-8" data-id="element-804">
                  <div data-id="element-805">
                    <h3 className="text-[#061D76] font-bold text-base md:text-lg mb-2" data-id="element-806">
                      {t('mobileAppPage.appFeatures.viewCards')}
                    </h3>
                  </div>
                  <div data-id="element-807">
                    <h3 className="text-[#061D76] font-bold text-base md:text-lg mb-2" data-id="element-808">
                      {t('mobileAppPage.appFeatures.manageCards')}
                    </h3>
                    <div className="text-[#061D76]/70 text-sm md:text-base leading-relaxed space-y-0.5" data-id="element-809">
                      <p data-id="element-810">{t('mobileAppPage.appFeatures.hideShow')}</p>
                      <p data-id="element-811">{t('mobileAppPage.appFeatures.activateDeactivate')}</p>
                      <p data-id="element-812">{t('mobileAppPage.appFeatures.cancelCard')}</p>
                    </div>
                  </div>
                  <div data-id="element-813">
                    <h3 className="text-[#061D76] font-bold text-base md:text-lg mb-2" data-id="element-814">
                      {t('mobileAppPage.appFeatures.accountSummary')}
                    </h3>
                    <div className="text-[#061D76]/70 text-sm md:text-base leading-relaxed space-y-0.5" data-id="element-815">
                      <p data-id="element-816">{t('mobileAppPage.appFeatures.limit')}</p>
                      <p data-id="element-817">{t('mobileAppPage.appFeatures.totalBalance')}</p>
                      <p data-id="element-818">{t('mobileAppPage.appFeatures.availableToSpend')}</p>
                      <p data-id="element-819">{t('mobileAppPage.appFeatures.minimumDue')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - vertically centered */}
              <div className={`flex-1 flex items-center md:pl-4 order-2 md:order-3 reveal-hidden ${featuresVisible ? 'animate-fade-up delay-400' : ''}`} data-id="element-820">
                <div className="space-y-6 py-8" data-id="element-821">
                  <div data-id="element-822">
                    <h3 className="text-[#061D76] font-bold text-base md:text-lg mb-2" data-id="element-823">
                      {t('mobileAppPage.appFeatures.billingCycle')}
                    </h3>
                  </div>
                  <div data-id="element-824">
                    <h3 className="text-[#061D76] font-bold text-base md:text-lg mb-2" data-id="element-825">
                      {t('mobileAppPage.appFeatures.transactionSummary')}
                    </h3>
                    <div className="text-[#061D76]/70 text-sm md:text-base leading-relaxed space-y-0.5" data-id="element-826">
                      <p data-id="element-827">{t('mobileAppPage.appFeatures.latestTransactions')}</p>
                      <p data-id="element-828">{t('mobileAppPage.appFeatures.transactionFilter')}</p>
                      <p data-id="element-829">{t('mobileAppPage.appFeatures.transactionHistory')}</p>
                      <p data-id="element-830">{t('mobileAppPage.appFeatures.disputeTransaction')}</p>
                      <p data-id="element-831">{t('mobileAppPage.appFeatures.pinChange')}</p>
                      <p data-id="element-832">{t('mobileAppPage.appFeatures.requestEstatement')}</p>
                      <p data-id="element-833">{t('mobileAppPage.appFeatures.spendingBehavior')}</p>
                      <p data-id="element-834">{t('mobileAppPage.appFeatures.biometricAuth')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Center on desktop, Bottom on mobile - Phone Mockup */}
              <div className={`flex-shrink-0 flex justify-center items-end mx-4 md:mx-8 order-3 md:order-2 reveal-hidden ${featuresVisible ? 'animate-fade-up delay-300' : ''}`} style={{
              marginBottom: '-1px'
            }} data-id="element-835">
                <img src="https://cdn.magicpatterns.com/uploads/5zs6REDZjgfxj1fkUDvj2L/phone.png" alt="Mobile App Preview" className="w-[500px] md:w-[600px] lg:w-[700px] h-auto block" data-id="element-836" />
              </div>
            </div>
          </div>
        </section>

        {/* 4. Join Our Bold Thinkers CTA */}
        <section className="relative py-24 text-white overflow-hidden" dir={direction} ref={ctaRef} aria-label="Call to Action" data-id="element-837">
          {/* Background Image */}
          <div className="absolute inset-0 bg-cover bg-center" role="presentation" aria-hidden="true" style={{
          backgroundImage: "url('https://cdn.magicpatterns.com/uploads/aNushftuwBdDBhG34PgQWF/Trusted.jpg')"
        }} data-id="element-838"></div>

          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-839">
            <div data-id="element-840">
              <h2 className={`text-4xl md:text-5xl font-bold mb-6 whitespace-pre-line reveal-hidden ${ctaVisible ? 'animate-fade-left' : ''}`} data-id="element-841">
                {t('aboutPage.cta.heading')}
              </h2>
              <p className={`text-white max-w-[950px] text-lg mb-8 leading-relaxed reveal-hidden ${ctaVisible ? 'animate-fade-left delay-200' : ''}`} data-id="element-842">
                {t('aboutPage.cta.description')}
              </p>
              <button className={`btn-primary reveal-hidden ${ctaVisible ? 'animate-fade-up delay-400' : ''}`} data-id="element-843">
                {t('aboutPage.cta.button')}
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-844" />
    </div>;
}